import { XLSX_PREVIEW_VIRTUAL_ROW_HEIGHT } from 'components/SpreadsheetNotes/spreadsheet-note/xlsxPreviewVirtualRowHeight';

const XLSX_PREVIEW_TESTID = 'spreadsheet-note-xlsx-preview';

function normalizeCellRef(ref: string): string {
  const m = ref.trim().match(/^([A-Za-z]+)(\d+)$/);
  return m ? `${m[1].toUpperCase()}${m[2]}` : ref.trim().toUpperCase();
}

function getExcelRowFromRef(ref: string): number | null {
  const m = ref.trim().match(/^[A-Za-z]+(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

function tryScrollCellIntoViewInRoots(roots: NodeListOf<Element> | Element[], cellKey: string): boolean {
  const list = Array.from(roots);
  for (let i = 0; i < list.length; i += 1) {
    const el = list[i].querySelector<HTMLElement>(`[id="CELL-${cellKey}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      return true;
    }
  }
  return false;
}

/**
 * For virtualized xlsx grids the target <td> may not exist until the scroll host moves.
 * Nudge scrollTop using the Excel row index from the cell ref, then retries can find the node.
 */
function tryApproximateVirtualScroll(roots: NodeListOf<Element> | Element[], cellKey: string): boolean {
  const excelRow = getExcelRowFromRef(cellKey);
  if (excelRow == null) {
    return false;
  }
  const list = Array.from(roots);
  for (let i = 0; i < list.length; i += 1) {
    const host = list[i].querySelector<HTMLElement>('.data-grid-container > div');
    if (host && host.scrollHeight > host.clientHeight) {
      const { overflowY } = getComputedStyle(host);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        const top = Math.max(0, (excelRow - 1) * XLSX_PREVIEW_VIRTUAL_ROW_HEIGHT - host.clientHeight * 0.35);
        host.scrollTo({ top, behavior: 'smooth' });
        return true;
      }
    }
  }
  return false;
}

/**
 * Scroll the xlsx preview so a mapped cell (e.g. cap table → document) is visible.
 * Scoped to [data-testid=spreadsheet-note-xlsx-preview] so IDs are not confused with other spreadsheets.
 */
export function scheduleScrollMappedXlsxCellIntoView(rawCellRef: string): void {
  const cellKey = normalizeCellRef(rawCellRef);

  const roots = () => document.querySelectorAll(`[data-testid="${XLSX_PREVIEW_TESTID}"]`);

  const attempt = (): boolean => {
    const r = roots();
    return r.length > 0 && tryScrollCellIntoViewInRoots(r, cellKey);
  };

  const nudgeVirtual = (): boolean => {
    const r = roots();
    return r.length > 0 && tryApproximateVirtualScroll(r, cellKey);
  };

  if (attempt()) {
    return;
  }
  nudgeVirtual();

  requestAnimationFrame(() => {
    if (attempt()) {
      return;
    }
    requestAnimationFrame(() => {
      attempt();
    });
  });

  window.setTimeout(() => {
    if (!attempt()) {
      nudgeVirtual();
    }
  }, 50);

  [120, 280, 500].forEach(ms => {
    window.setTimeout(() => {
      attempt();
    }, ms);
  });
}
